import { useCallback, useEffect, useState } from 'react';
import { api, type Destination } from '../api/client';

/**
 * Where alerts go (ADR-0020).
 *
 * This replaces a Worker secret that only the person holding the Cloudflare account could set, from
 * a terminal, once per environment, with no validation and no feedback. It was set to a bare path and
 * failed silently every fifteen minutes for a day, which is the strongest argument for this screen
 * that could be made.
 *
 * Two things it must never do:
 *
 *  - **Show an address.** A webhook URL is a bearer credential. The server never sends one, so there
 *    is nothing here to render — the label is what identifies a destination, which is why ADR-0020
 *    requires labels to be authored rather than derived from the URL.
 *  - **Claim something it has not checked.** Adding a destination probes it and stores it only if it
 *    answers. So "added" here means "we sent it a message and it accepted", never "we wrote it down".
 */
export function Destinations() {
  const [items, setItems] = useState<Destination[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState('');
  const [minUrgency, setMinUrgency] = useState<'page' | 'notice'>('notice');

  /**
   * Re-read the server's list rather than patching local state after a mutation.
   *
   * Every console bug so far has been the interface asserting something it had not checked, and
   * splicing an assumed row into an array is exactly that shape — it would show a destination as
   * added whether or not the write landed.
   */
  const load = useCallback(async () => {
    try {
      setItems((await api.destinations()).destinations);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const created = await api.addDestination({ label, address, minUrgency });
      // Cleared only on success — a rejected URL stays in the box so it can be corrected rather
      // than retyped from wherever it was copied.
      setLabel('');
      setAddress('');
      setNote({ text: `“${created.label}” received a test message and is now live.`, tone: 'ok' });
      await load();
    } catch (err) {
      setNote({
        text: err instanceof Error ? err.message : 'Could not add that destination.',
        tone: 'warn',
      });
    } finally {
      setBusy(false);
    }
  }

  async function setUrgency(d: Destination, next: 'page' | 'notice') {
    setBusy(true);
    try {
      await api.updateDestination(d.id, { minUrgency: next });
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function remove(d: Destination) {
    setBusy(true);
    setNote(null);
    try {
      await api.removeDestination(d.id);
      setNote({ text: `“${d.label}” will no longer receive alerts.`, tone: 'ok' });
      await load();
    } catch (err) {
      setNote({
        text: err instanceof Error ? err.message : 'Could not remove that destination.',
        tone: 'warn',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h3>Alert destinations</h3>
      <p className="muted">
        Where Guard Dog sends alerts. Paste a Slack or Discord incoming-webhook URL — we send it a
        test message before saving, so a destination that appears here is one that answered.
      </p>

      {items && items.length === 0 && (
        <p className="muted">
          No destinations yet. Browser notifications are configured separately, below.
        </p>
      )}

      {items && items.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Channel</th>
              <th>Sends</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td>
                  {d.label}
                  {d.disabled && <span className="badge badge-off"> paused</span>}
                </td>
                <td className="muted">{d.channel}</td>
                <td>
                  {/* The setting that actually bit: a destination left at `page` silently misses
                      every notice, including "a site stopped reporting". */}
                  <select
                    value={d.minUrgency}
                    disabled={busy}
                    aria-label={`What ${d.label} receives`}
                    onChange={(e) => void setUrgency(d, e.target.value as 'page' | 'notice')}
                  >
                    <option value="notice">Everything</option>
                    <option value="page">Critical only</option>
                  </select>
                </td>
                <td>
                  <button
                    type="button"
                    className="copy-btn"
                    disabled={busy}
                    onClick={() => void remove(d)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form onSubmit={(e) => void add(e)}>
        <label htmlFor="dest-label">Name</label>
        <input
          id="dest-label"
          value={label}
          placeholder="Ops channel"
          onChange={(e) => setLabel(e.target.value)}
        />
        <label htmlFor="dest-address">Webhook URL</label>
        <input
          id="dest-address"
          type="url"
          value={address}
          placeholder="https://hooks.slack.com/services/…"
          onChange={(e) => setAddress(e.target.value)}
        />
        <label htmlFor="dest-urgency">Sends</label>
        <select
          id="dest-urgency"
          value={minUrgency}
          onChange={(e) => setMinUrgency(e.target.value as 'page' | 'notice')}
        >
          <option value="notice">Everything</option>
          <option value="page">Critical only</option>
        </select>
        <button type="submit" disabled={busy || !label.trim() || !address.trim()}>
          {busy ? 'Testing…' : 'Add and test'}
        </button>
      </form>

      {note && <p className={note.tone === 'ok' ? 'okbar' : 'warnbar'}>{note.text}</p>}
    </section>
  );
}
